import { attendanceKeyOf, type ScheduledTask } from "./study-plan-state";
import { getStudyPlan, STUDY_PLAN_IDS, type StudyPlanId } from "./study-plan";
import { buildScheduledTasks } from "./study-plan-state";

export interface StudyPlanSyncCandidate {
  planId: StudyPlanId;
  taskKey: string;
  title: string;
  codeLang?: string;
  completedAt: string | null;
}

/** 선택한 계획들의 완료 기록을 합쳐 각 계획의 첫 회독에만 반영한다. */
export function studyPlanSyncCandidates(
  tasksByPlan: Partial<Record<StudyPlanId, ScheduledTask[]>>,
): StudyPlanSyncCandidate[] {
  const completed = new Map<
    string,
    { planIds: Set<StudyPlanId>; at: string | null }
  >();
  for (const tasks of Object.values(tasksByPlan))
    for (const task of tasks ?? []) {
      if (!task.slug || !task.attendanceEligible || !task.done || task.skipped)
        continue;
      const key = attendanceKeyOf(task.slug, task.codeLang);
      const existing = completed.get(key);
      if (existing) {
        existing.planIds.add(task.planId);
        if (
          task.completedAtISO &&
          (!existing.at || task.completedAtISO < existing.at)
        )
          existing.at = task.completedAtISO;
      } else
        completed.set(key, {
          planIds: new Set([task.planId]),
          at: task.completedAtISO,
        });
    }
  const candidates: StudyPlanSyncCandidate[] = [];
  for (const tasks of Object.values(tasksByPlan)) {
    const seen = new Set<string>();
    for (const task of tasks ?? []) {
      if (!task.slug || !task.attendanceEligible) continue;
      const key = attendanceKeyOf(task.slug, task.codeLang);
      if (seen.has(key)) continue;
      seen.add(key);
      const source = completed.get(key);
      if (
        !source ||
        task.done ||
        task.skipped ||
        ![...source.planIds].some((id) => id !== task.planId)
      )
        continue;
      candidates.push({
        planId: task.planId,
        taskKey: task.taskKey,
        title: task.title,
        codeLang: task.codeLang,
        completedAt: source.at,
      });
    }
  }
  return candidates;
}

/** 구독 직후 새 계획에 반영할 완료가 있을 때만 안내한다. */
export function subscriptionSyncPlanIds(
  newPlanId: StudyPlanId,
  plans: Record<StudyPlanId, import("./study-plan-state").PlanDynamicState>,
  examSessionId: string,
): StudyPlanId[] {
  const selected = STUDY_PLAN_IDS.filter((id) => plans[id]?.subscribed && getStudyPlan(id, examSessionId));
  if (selected.length < 2 || !selected.includes(newPlanId)) return [];
  const tasks: Partial<Record<StudyPlanId, ScheduledTask[]>> = {};
  for (const id of selected) tasks[id] = buildScheduledTasks(getStudyPlan(id, examSessionId)!, plans[id]);
  return studyPlanSyncCandidates(tasks).some((candidate) => candidate.planId === newPlanId) ? selected : [];
}
